import React from 'react';
import type { EventDto } from '@odo/shared-ui';

/** How many dots the column shows at most - a VIEWPORT, not a cap. Past three
 *  events the window slides and the count label spells out the position, so ten
 *  events render exactly as tall as three.
 *
 *  Three rows of 10px with 2px gaps come to 34px, and the label adds ~12px: 46px
 *  inside a ~62px column (a 38px content box plus its py-3). That slack is why
 *  the rows are back to 10px rather than the 8px five of them needed. */
const MAX_DOTS = 3;

interface EventRotationControlsProps {
    readonly events: readonly EventDto[];
    readonly activeIndex: number;
    readonly onSelect: (index: number) => void;
}

/**
 * First index of the visible window - the active dot kept centred, clamped so
 * the window never runs off either end (which would otherwise render blanks at
 * the extremes, where the list has no more events to show).
 */
function windowStart(activeIndex: number, count: number, size: number): number {
    if (count <= size) return 0;
    const before = Math.floor(size / 2);
    return Math.min(Math.max(activeIndex - before, 0), count - size);
}

interface DotProps {
    readonly event: EventDto;
    readonly index: number;
    readonly count: number;
    readonly isActive: boolean;
    /** Sits at a window edge with more events beyond it. */
    readonly isFading: boolean;
    readonly onSelect: (index: number) => void;
}

// The row is a constant 8x20 whatever the dot inside it does, so neither the
// column's height nor the hit target changes as the window slides.
const Dot: React.FC<DotProps> = ({ event, index, count, isActive, isFading, onSelect }) => {
    const size = isFading ? 'h-1 w-1' : 'h-1.5 w-1.5';
    const opacity = isActive ? 'opacity-100' : isFading ? 'opacity-25' : 'opacity-40';

    return (
        <button
            type="button"
            // The banner behind this opens the detail modal on click.
            onClick={(e) => {
                e.stopPropagation();
                onSelect(index);
            }}
            // The window hides the full extent, so the position rides along
            // here - it is the only place a screen reader can still hear it.
            aria-label={`Show ${event.title} (${index + 1} of ${count})`}
            aria-current={isActive}
            className="flex h-2.5 w-5 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-current"
        >
            <span
                className={`block rounded-full bg-current transition-all duration-150 ${size} ${opacity} ${
                    isActive || isFading ? '' : 'hover:opacity-70'
                }`}
            />
        </button>
    );
};

/** Position only - picking one both jumps to it and stops the auto-advance,
 *  which is what a reader wanting the strip to hold still reaches for now that
 *  there is no separate pause control. */
export const EventRotationControls: React.FC<EventRotationControlsProps> = ({
    events,
    activeIndex,
    onSelect
}) => {
    const count = events.length;
    // Once the window hides part of the list the dots can no longer answer "how
    // many" - that is exactly when the count label earns its row.
    const windowed = count > MAX_DOTS;
    const size = Math.min(count, MAX_DOTS);
    const start = windowStart(activeIndex, count, size);

    return (
        // `bg-current` inherits the banner's text colour, so one rule covers all
        // twenty theme presets without a per-theme entry.
        <span
            className="flex flex-col items-center gap-0.5"
            role="group"
            aria-label={`Event ${activeIndex + 1} of ${count}`}
        >
            {events.slice(start, start + size).map((event, offset) => {
                const index = start + offset;
                // A shrunken dot at either end is the only hint that the list
                // continues past the window - without it, ten events look like five.
                const moreBefore = offset === 0 && start > 0;
                const moreAfter = offset === size - 1 && start + size < count;

                return (
                    <Dot
                        key={event.id}
                        event={event}
                        index={index}
                        count={count}
                        isActive={index === activeIndex}
                        isFading={moreBefore || moreAfter}
                        onSelect={onSelect}
                    />
                );
            })}

            {windowed && (
                <span
                    aria-hidden="true"
                    className="mt-[2px] text-[9px] font-semibold leading-none tabular-nums opacity-60"
                >
                    {activeIndex + 1}/{count}
                </span>
            )}
        </span>
    );
};

export default EventRotationControls;
